import * as React from 'react';
import { useEffect, useRef } from 'react';
import { ErrorBoundary } from '@sentry/react';
import { cn } from '../../../utils/ClassNamesUtils';
import { OSCAR_CONTENT_INSET, OSCAR_READING_MEASURE } from '../../constants/AIAgentConstants';
import { AgentUnsentMessages } from '../../../components/agent_chat/useAgentChat';
import { AgentTranscriptEntry } from '../../../types/agentHarness';
import OscarDisclosure from '../shared/OscarDisclosure';
import OscarFileCard from '../shared/OscarFileCard';
import OscarMarkdown from '../shared/OscarMarkdown';
import OscarThinkingIndicator from '../shared/OscarThinkingIndicator';
import OscarUnsentMessage from '../shared/OscarUnsentMessage';
import { useOscarTurnStatus } from '../../hooks/useOscarTurnStatus';
import { humanizeAgentError } from '../../../utils/AgentErrorUtils';
import OscarToolTrace from './OscarToolTrace';
import OscarApprovalQueue from '../oscar_question_cards/OscarApprovalQueue';

type ToolEntry = Extract<AgentTranscriptEntry, { kind: 'tool' }>;
type ApprovalEntry = Extract<AgentTranscriptEntry, { kind: 'approval' }>;

type TranscriptGroup =
	| { kind: 'entry'; id: string; entry: AgentTranscriptEntry }
	| { kind: 'tools'; id: string; tools: ToolEntry[] }
	| { kind: 'approvals'; id: string; approvals: ApprovalEntry[] };

/**
 * Collapses each run of consecutive tool calls into one group. They arrive
 * adjacent in the stream, so a run is exactly what the agent did between two
 * things it said.
 */
/**
 * A user message starts a turn; the tool calls, approvals and answer after it
 * belong to that turn. Everything used to sit at the same `gap-s`, so a
 * conversation was one undifferentiated stack with no way to see where a
 * question ended and the next began.
 */
const startsTurn = (group: TranscriptGroup, index: number): boolean =>
	index > 0 && group.kind === 'entry' && group.entry.kind === 'user';

const groupEntries = (entries: AgentTranscriptEntry[]): TranscriptGroup[] => {
	const groups: TranscriptGroup[] = [];
	entries.forEach((entry) => {
		const last = groups[groups.length - 1];
		if (entry.kind === 'tool') {
			if (last && last.kind === 'tools') {
				last.tools.push(entry);
				return;
			}
			groups.push({ kind: 'tools', id: entry.id, tools: [entry] });
			return;
		}
		// One question can queue a write per record, and stacked vertically those
		// bury everything below them.
		if (entry.kind === 'approval') {
			if (last && last.kind === 'approvals') {
				last.approvals.push(entry);
				return;
			}
			groups.push({ kind: 'approvals', id: entry.id, approvals: [entry] });
			return;
		}
		groups.push({ kind: 'entry', id: entry.id, entry });
	});
	return groups;
};

interface OscarTranscriptProps {
	/** Straight from `useAgentChat().entries`. */
	entries: AgentTranscriptEntry[];
	/** `useAgentChat().busy` — drives the thinking indicator. */
	busy?: boolean;
	/** `useAgentChat().turnStartedAt` — drives the elapsed timer. */
	turnStartedAt?: number;
	/** Letter shown in the avatar beside the user's prompt. */
	userInitial?: string;
	onResolveApproval?: (approvalId: string, approved: boolean) => void | Promise<boolean>;
	/** `downloadAgentFile`, injected by the container. */
	onDownloadFile?: (url: string) => Promise<Blob>;
	/** `useAgentChat().unsentMessages` — puts a retry under each failed message. */
	unsentMessages?: AgentUnsentMessages;
	/** `useAgentChat().retrySend`. Omit while the connection strip owns the retry. */
	onRetrySend?: (messageId: string) => void;
	/**
	 * Max width of the reading column. The drawer caps it so lines stay
	 * comfortable; fullscreen passes `OSCAR_FULL_MEASURE` and takes the room.
	 */
	measure?: string;
	className?: string;
}

/**
 * Figma node 14998:125498 — the conversation pane. The user's prompt is a grey
 * card with an avatar; the agent's reply is plain paragraphs, so long answers
 * read as prose rather than chat bubbles.
 *
 * Only the `user` and `assistant` rows are designed. The stream also emits tool
 * calls, approvals, files and notices; those are rendered plainly from existing
 * tokens and are flagged as design gaps in DESIGN_TOKEN_GAPS.md.
 *
 * Message bodies are the one place here that genuinely uses `<p>` — an agent
 * answer is real prose. `mb-0` is needed because `src/index.less` gives every
 * `p` a 1em bottom margin app-wide; spacing comes from the parent's `gap`.
 */
export const OscarTranscript = ({
	entries,
	busy,
	turnStartedAt = 0,
	userInitial = '?',
	onResolveApproval,
	onDownloadFile,
	unsentMessages,
	onRetrySend,
	measure = OSCAR_READING_MEASURE,
	className,
}: OscarTranscriptProps) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const pinnedToBottomRef = useRef(true);
	const turn = useOscarTurnStatus(!!busy, entries, turnStartedAt);

	// Answers stream in token by token, so the view has to follow — but only if
	// the reader is already at the bottom. Yanking someone who scrolled up to
	// re-read something is worse than not following at all.
	const handleScroll = () => {
		const el = scrollRef.current;
		if (!el) {
			return;
		}
		pinnedToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
	};

	useEffect(() => {
		const el = scrollRef.current;
		if (el && pinnedToBottomRef.current) {
			el.scrollTop = el.scrollHeight;
		}
	}, [entries, turn.label, turn.seconds, unsentMessages]);

	return (
		<div
			ref={scrollRef}
			onScroll={handleScroll}
			className={cn('flex min-h-0 w-full flex-1 flex-col overflow-y-auto py-l', className)}
		>
			{/* The scroller stays full width so the scrollbar sits at the panel's
			    edge; this inner column carries the reading measure and centres
			    itself, and full-bleed rows bleed to it rather than to the viewport. */}
			<div className={cn('mx-auto flex w-full flex-col gap-s', measure)}>
				{/* The transcript renders model-authored content — markdown, tables,
				    tool payloads — so a render throw here is a real possibility rather
				    than a theoretical one. Contained to the transcript: the header, the
				    composer and New chat stay usable, and in the dock's case a throw no
				    longer takes the app's top bar down with it on every page. Per-row
				    isolation would be better still and is worth doing; this is the
				    version that does not require reshaping the loop below. */}
				<ErrorBoundary
					fallback={
						<div className={cn('bodyregular text-character-secondary', OSCAR_CONTENT_INSET)}>
							This conversation could not be displayed. Starting a new chat should clear it.
						</div>
					}
				>
					{groupEntries(entries).map((group, index) => {
						if (group.kind === 'tools') {
							return (
								<div key={group.id} className={cn('flex w-full shrink-0', OSCAR_CONTENT_INSET)}>
									<OscarToolTrace tools={group.tools} />
								</div>
							);
						}
						if (group.kind === 'approvals') {
							return (
								// No inset here: the queue bleeds to both edges of the column and
								// pads its own header, so cards can scroll past the gutter.
								<div key={group.id} className="flex w-full min-w-0 shrink-0">
									<OscarApprovalQueue approvals={group.approvals} onResolve={onResolveApproval} />
								</div>
							);
						}
						const entry = group.entry;
						switch (entry.kind) {
							case 'user': {
								const unsent = unsentMessages ? unsentMessages[entry.id] : undefined;
								return (
									<React.Fragment key={entry.id}>
										<div
											className={cn(
												'flex w-full shrink-0 items-start gap-xs',
												OSCAR_CONTENT_INSET,
												startsTurn(group, index) && 'mt-l'
											)}
										>
											<div className="flex flex-1 flex-col justify-center gap-2xs rounded bg-grey-3 px-m py-xs">
												{/* The composer takes Shift+Enter for a newline and sends it, so the
										    transcript has to show it: plain HTML collapsed a pasted list
										    into one line. */}
												<p className="bodyregular mb-0 whitespace-pre-wrap break-words text-character-primary">
													{entry.text}
												</p>
											</div>
											<span
												aria-hidden="true"
												className="bodyregular flex size-8 shrink-0 items-center justify-center rounded-full border border-solid border-white bg-[#ff7a45] text-center text-white"
											>
												{userInitial}
											</span>
										</div>
										{unsent ? (
											<OscarUnsentMessage
												reason={unsent.reason}
												onRetry={onRetrySend ? () => onRetrySend(entry.id) : undefined}
												className={OSCAR_CONTENT_INSET}
											/>
										) : null}
									</React.Fragment>
								);
							}
							case 'assistant':
								// An assistant entry with nothing in it yet renders nothing — the
								// trailing indicator below is already saying what is happening, and
								// two spinners for one turn is worse than none.
								if (!entry.done && !entry.text) {
									return null;
								}
								return (
									<div
										key={entry.id}
										className={cn('flex w-full shrink-0 flex-col gap-xs', OSCAR_CONTENT_INSET)}
									>
										{entry.thinking ? (
											<OscarDisclosure label="reasoning">
												<span className="footnoteregular whitespace-pre-wrap text-character-secondary">
													{entry.thinking}
												</span>
											</OscarDisclosure>
										) : null}
										<OscarMarkdown>{entry.text}</OscarMarkdown>
									</div>
								);
							case 'file':
								return (
									<div key={entry.id} className={cn('flex w-full shrink-0', OSCAR_CONTENT_INSET)}>
										<OscarFileCard
											name={entry.name}
											size={entry.size}
											url={entry.url}
											onDownload={onDownloadFile}
										/>
									</div>
								);
							case 'notice':
								return (
									<div key={entry.id} className={cn('flex w-full shrink-0', OSCAR_CONTENT_INSET)}>
										<span
											className={cn(
												'footnoteregular',
												entry.severity === 'error'
													? 'text-character-danger'
													: 'text-character-secondary'
											)}
										>
											{entry.severity === 'error' ? humanizeAgentError(entry.text) : entry.text}
										</span>
									</div>
								);
							default:
								return null;
						}
					})}
				</ErrorBoundary>
				{turn.label ? (
					<OscarThinkingIndicator
						label={turn.label}
						seconds={turn.seconds}
						className={OSCAR_CONTENT_INSET}
					/>
				) : null}
			</div>
		</div>
	);
};

export default OscarTranscript;
